import { StrictMode } from 'react';

import { createRoot } from 'react-dom/client';

import { config, logger } from '@tankef/env';

// Initialize i18n before app renders
logger.info('[Main] Initializing i18n...');
import '@tankef/i18n';
logger.info('[Main] i18n loaded');

import { App } from './app/App';
import { registerOnboardingFlows } from './domains/identity/onboarding/flows';
import { reportWebVitals } from './shared/analytics/web-vitals';
import './shared/lib/media-stream-tracker';
import '@tankef/ui/styles/semantic-vars';
import './index.css';

logger.info('[Main] Starting app initialization...');

async function init() {
  try {
    // Defense in depth: validate-env.ts already enforces enableMockApi=false
    // in environments.json, but MSW must NEVER run in a production build
    // even if a misconfigured local override flips the flag.
    if (config.enableMockApi && import.meta.env.PROD) {
      logger.warn('[Main] enableMockApi=true ignored in production build');
    } else if (config.enableMockApi) {
      logger.info('[Main] Enabling MSW (mock API)...');
      const { startMockServer } = await import('@tankef/api-client/mocks');
      await startMockServer();
      logger.info('[Main] MSW started');
    } else {
      logger.info('[Main] Using real backend API at', config.apiUrl);
    }

    // Registrar flujos de onboarding (F1 PM) antes de render — el shell los
    // resuelve vía onboardingFlowRegistry. PF siempre; PM en F3+ tras flag.
    registerOnboardingFlows();

    logger.info('[Main] Rendering React app...');
    const rootEl = document.getElementById('root');
    if (!rootEl) {
      throw new Error('Root element not found!');
    }

    createRoot(rootEl).render(
      <StrictMode>
        <App />
      </StrictMode>
    );
    logger.info('[Main] React app rendered successfully');
  } catch (err) {
    logger.error('[Main] Failed to initialize app:', err);
    const errorMsg = err instanceof Error ? err.message : String(err);
    const stack = err instanceof Error ? err.stack : '';
    // Built via textContent to avoid injecting attacker-influenced error
    // messages as HTML if init() ever pulls remote input in the future.
    const container = document.createElement('div');
    container.style.cssText =
      'font-family: monospace; padding: 20px; color: #d32f2f; background: #ffebee; white-space: pre-wrap; line-height: 1.6;';
    const heading = document.createElement('h1');
    heading.style.cssText = 'margin: 0 0 10px 0;';
    heading.textContent = 'Failed to initialize app';
    const message = document.createElement('strong');
    message.style.color = '#b71c1c';
    message.textContent = errorMsg;
    const stackEl = document.createElement('pre');
    stackEl.style.cssText =
      'margin-top: 15px; font-size: 12px; color: #666; background: #fff; padding: 10px; border: 1px solid #ddd; overflow-x: auto;';
    stackEl.textContent = stack ?? '';
    container.append(heading, message, stackEl);
    document.body.replaceChildren(container);
  }
}

init();

// Web Vitals — reporta LCP, CLS, TTFB, FID, INP a GTM dataLayer
reportWebVitals();

// Captura promesas no manejadas — Sentry las recibe por su SDK global,
// aquí las empujamos también a GTM para tenerlas en el dataLayer.
window.addEventListener('unhandledrejection', (event: PromiseRejectionEvent) => {
  const reason = event.reason;
  const message = reason instanceof Error ? reason.message : String(reason ?? 'unknown');
  logger.warn('[Main] Unhandled promise rejection', { message });
  window.dataLayer?.push({
    event: 'error_unhandled_rejection',
    errorMessage: message,
  });
});

// Safari Back-Forward Cache: cuando el usuario presiona atrás/adelante,
// Safari restaura una snapshot congelada sin hacer un nuevo request HTTP.
// Detectamos ese caso y forzamos un reload para que siempre corra la versión actual.
window.addEventListener('pageshow', event => {
  if (event.persisted) window.location.reload();
});
